<template>
  <div class="content">
    <div class="header d-flex align-items-center profile-header">
      <!-- Mask -->
      <span class="mask bg-gradient-info"></span>
      <!-- Header container -->
      <div class="container-fluid d-flex align-items-center">
        <div class="row">
          <div class="col-lg-12">
            <router-link :to="{ name: 'VenueDetails', params: { id: venueId }}">
              <h1 class="display-3 text-white">{{ venue.name }}</h1>
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-12">
          <h6 class="h2 text-white d-inline-block mb-0">
            {{ $route.meta && $route.meta.name ? $route.meta.name : $route.name }}
          </h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-breadcrumb></route-breadcrumb>
          </nav>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--6">
      <div class="row">
        <div class="col-7">
          <card>
            <div slot="header" class="row align-items-center">
              <div class="col-12">
                <div class="row">
                    <div class="col-6">
                        <h3 class="mb-0">Quote Annex</h3>
                    </div>
                    <div class="col-6 text-right">
                        <button 
                            class="btn btn-primary btn-sm" 
                            @click="saveQuoteAnnex" 
                            :loading="saving"
                            :disabled="saving"
                        >
                            Save
                        </button>
                    </div>
                </div>
              </div>
            </div>
            <div v-loading="loading">
              <quote-annex-form
                v-model="additionalTerms"
                :default-terms="defaultTerms"
                @save="saveQuoteAnnex"
                ref="quoteAnnexForm"
              />
              
              <!-- Save button at the bottom of the form -->
              <div class="text-center mt-4 pt-3" style="border-top: 1px solid #dee2e6;">
                <button 
                  class="btn btn-primary" 
                  @click="saveQuoteAnnex" 
                  :loading="saving"
                  :disabled="saving"
                >
                  <i class="fa fa-save mr-2"></i>
                  {{ saving ? 'Saving...' : 'Save Quote Annex' }}
                </button>
              </div>
            </div>
          </card>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Card from "@/components/Cards/Card.vue";
import QuoteAnnexForm from "@/components/Forms/QuoteAnnexForm.vue";
import { mapState } from "vuex";
import VenueService from "@/services/venue.service";

export default {
  components: {
    Card,
    QuoteAnnexForm,
  },
  data() {
    return {
      venueId: parseInt(this.$route.params.id),
      loading: true,
      saving: false,
      additionalTerms: [],
      defaultTerms: [
        { name: "Venue Name / ABN", content: "", order: 1 },
        { name: "Minimum Spend", content: "", order: 2 },
        { name: "Venue Hire Fee", content: "", order: 3 },
        { name: "Deposit & Payment Schedule", content: "", order: 4 },
        { name: "Cancellation Policy", content: "", order: 5 },
        { name: "Attrition / Slippage Policy", content: "", order: 6 },
        { name: "Public Holiday Surcharges", content: "", order: 7 },
        { name: "Noise / Curfew Restrictions", content: "", order: 8 },
        { name: "Bump-In / Bump-Out Rules", content: "", order: 9 },
        { name: "Security Requirements", content: "", order: 10 },
        { name: "AV & Contractor Rules", content: "", order: 11 },
        { name: "Insurance Requirements", content: "", order: 12 },
        { name: "Catering Rules", content: "", order: 13 },
        { name: "Cleaning / Damage Fees", content: "", order: 14 },
        { name: "Styling / Signage Restrictions", content: "", order: 15 }
      ]
    };
  },
  async created() {
    // Load venue details if not already loaded
    await this.$store.dispatch("venues/getSingle", { id: this.venueId });
    
    // Load existing quote annex data if available
    await this.loadQuoteAnnex();
    
    this.loading = false;
  },
  computed: {
    venue() {
      return this.$store.getters["venues/getVenueById"](this.venueId);
    },
  },
  methods: {
    async loadQuoteAnnex() {
      try {
        const response = await VenueService.getVenueAdditionalNotes(this.venueId);
        if (response && response.data && response.data.additional_terms && response.data.additional_terms.length > 0) {
          // Use API data if available
          this.additionalTerms = response.data.additional_terms
            .sort((a, b) => a.order - b.order)
            .map(term => ({ ...term, isEditing: false }));
        } else {
          // Use default structure if no data from API
          this.additionalTerms = this.defaultTerms.map(term => ({ ...term, isEditing: false }));
        }
        this.reorderTerms();
      } catch (error) {
        console.error('Error loading quote annex:', error);
        // Fallback to default structure on error
        this.additionalTerms = this.defaultTerms.map(term => ({ ...term, isEditing: false }));
        this.reorderTerms();
      }
    },

    reorderTerms() {
      // Update order numbers sequentially
      this.additionalTerms.forEach((term, index) => {
        term.order = index + 1;
      });
    },

    async saveQuoteAnnex() {
      this.saving = true;
      try {
        // Validate the form before saving
        const validation = this.$refs.quoteAnnexForm.validateTerms();
        
        if (!validation.isValid) {
          // Show validation errors to the user
          const errorCount = validation.errors.length;
          const errorList = validation.errors.map(error => 
            `• ${error.message}`
          ).join('\n');
          
          this.$toast.error(
            `Please fix ${errorCount} validation error${errorCount > 1 ? 's' : ''}:\n\n${errorList}\n\nNote: You can remove unwanted annex sections if they are not needed.`,
            {
              duration: 8000 // Show for 8 seconds for longer message
            }
          );
          this.saving = false;
          return;
        }
        
        // Get clean terms from the component
        const terms = this.$refs.quoteAnnexForm.getTerms();
        const payload = {
          additional_terms: terms
        };
        
        await VenueService.updateVenueAdditionalNotes(this.venueId, payload);
        
        // Clear validation errors on successful save
        this.$refs.quoteAnnexForm.clearValidationErrors();
        
        this.$toast.success('Quote Annex saved successfully!');
      } catch (error) {
        console.error('Error saving quote annex:', error);
        this.$toast.error('Failed to save Quote Annex. Please try again.');
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>